<template>
  <section class="promo__codes-form-wrap media-buying-modal">
    <Modal
      v-model:show="active"
      @close="close"
    >
      <template v-slot:header>
        <h5 class="modal-title">
          {{ $t(`promoCodes.addPromocodes.${id ? 'editPromocode' : 'title'}`) }}
        </h5>
      </template>
      <div>
        <form v-loading="loading">
          <div class="!tw-flex mb-16 mobile-flex-column">
            <UiSelect
              v-if="$store.state.profile.user.referral_program_enabled"
              v-model="sub_affiliate_id"
              :label="$t('mediaBuying.campaign.affiliate')"
              :placeholder="$t('mediaBuying.campaign.my_account')"
              :list="referrals"
              is-search
              @change="changeSubAffiliateId"
            />
            <div
              class="select-wrapper !tw-w-full ml-16"
              :class="{'ml-0': !$store.state.profile.user.referral_program_enabled}"
            >
              <UiSelect
                v-model="offer"
                :error="errors.affiliate_offer_id ? errors.affiliate_offer_id[1].message : ''"
                :label="$t('promoCodes.addPromocodes.offer')"
                :placeholder="$t('promoCodes.addPromocodes.selectOffer')"
                :list="offers"
                is-search
              />
            </div>
          </div>
          <UiInput
            v-model="form.promocode"
            v-model:error="errors.promo_code"
            class="mb-16"
            :label="$t('promoCodes.addPromocodes.promocode')"
            :placeholder="$t('promoCodes.addPromocodes.promocode')"
            :disabled="id !== ''"
            error-clear-on-change
          />
          <ElPopover
            ref="popover"
            v-model:visible="warningOpen"
            placement="top-end"
            trigger="manual"
            width="320"
          >
            <p>{{ $t('promoCodes.addPromocodes.changeSubId') }}</p>
            <button
              class="btn btn-outline-primary btn-md"
              @click="warningOpen = false"
            >
              {{ $t('offerRequest.btnCancel') }}
            </button>
            <button
              class="btn btn-primary btn-md !tw-ml-1rem"
              @click="confirmChanges(); warningOpen = false"
            >
              {{ $t('offerRequest.btnConfirm') }}
            </button>

            <template v-slot:reference>
              <div />
            </template>
          </ElPopover>

          <UiSelect
            v-model="traffic_source_id"
            :label="$t('trafficSources.trafficSource')"
            :placeholder="$t('offerRequest.noTrafficSources')"
            :list="sources"
            is-search
            class="mb-16"
            @change="changeSource"
          />
          <div class="row row-wrap__subid">
            <div
              v-for="(item, i) in form.subids"
              :key="i"
              class="mb-16 col-6 col-6__add-subid"
            >
              <UiInputSubid
                v-model="item.value"
                :label="`SubID ${form.subids.indexOf(item) + 1}`"
                :placeholder="$t('trafficSources.postbacks.notSpecified')"
                :can-delete="form.subids.indexOf(item) === form.subids.length - 1 && form.subids.length > 1"
                @delete="popSubIds"
              />
            </div>
            <div class="col-6 !tw-flex !tw-items-end col-6__add-subid">
              <button
                :disabled="form.subids.length >= 5"
                href="#"
                class="btn btn-block btn-outline-primary btn-sm mb-16"
                @click.prevent="form.subids.length < 5 && addSubId()"
              >
                + {{ $t('promoCodes.addPromocodes.addAnotherSubid') }}
              </button>
            </div>
          </div>
        </form>
      </div>
      <template v-slot:footer>
        <div class="!tw-text-right !tw-flex !tw-items-baseline">
          <a
            class="btn btn-outline-primary btn-sm"
            @click="close"
          >
            {{ $t('promoCodes.addPromocodes.cancel') }}
          </a>
          <a
            class="btn btn-primary btn-sm !tw-ml-1rem"
            @click="send"
          >
            <span v-if="!loading">
              {{ $t(`promoCodes.addPromocodes.${id ? 'send' : 'title'}`) }}
            </span>
            <i
              v-if="loading"
              class="fas fa-spinner spinner-hide"
            />
          </a>
        </div>
      </template>     
    </Modal>
  </section>
</template>

<script>
import { Modal } from '@/components';
import _ from '@/lib/lodash';

export default {
  components: {
    Modal,
  },
  emits: ['addPromoCode'],
  data() {
    return {
      active: false,
      offer: '',
      changed: false,
      sending: false,
      traffic_source_id: '',
      sub_affiliate_id: '',
      form: {
        promocode: '',
        subids: [{ value: '' }],
      },
      errors: {},
      loading: false,
      id: '',
      warningOpen: false,
    };
  },
  computed: {
    offers() {
      let array = [];

      if (this.sub_affiliate_id !== '' && !this.loading) {
        array = this.$store.state.offers.my;
      } else {
        array = this.$store.state.promocode.offers;
      }
      return array.map((e) => ({
        id: e.id,
        text: e.name,
      }));
    },
    sources() {
      return this.$store.state.trafficSource.list.map((e) => ({
        id: e.data.id,
        text: e.name,
      }))
        .concat({
          id: '',
          text: this.$t('offerRequest.noTrafficSources'),
        });
    },
    referrals() {
      return this.$store.state.subaffiliates.all.map((e) => ({
        id: e.data.id,
        text: e.email,
      })).concat({ id: '', text: this.$t('mediaBuying.campaign.my_account') });
    },
  },
  watch: {
    traffic_source_id(id) {
      if (!this.id) {
        this.form.subids = [];
        const source = this.$store.state.trafficSource.list.filter((e) => e.data.id === id)[0];
        if (!source) {
          this.form.subids = [{ value: '' }];
          return;
        }
        // Push subids
        Object.keys(source)
          .map((k) => {
            if (k.startsWith('subid')) {
              if (source[k]) {
                this.form.subids.push({
                  value: source[k],
                });
              } else {
                this.form.subids.push({
                  value: '',
                });
              }
            }
          });
        // reduce subids
        for (let i = this.form.subids.length - 1; i >= 1; i--) {
          if (!this.form.subids[i].value) {
            this.form.subids.pop();
          } else {
            break;
          }
        }
      }
    },
  },
  methods: {
    open(row) {
      // this.$store.dispatch('getPromocodeOffers');
      this.reset();
      this.active = true;
      this.loading = true;
      if (row.data) {
        const id = row.affiliate_id ? { sub_affiliate_id: row.sub_affiliate_id } : {};
        const offers = this.$store.dispatch('getAffiliateOffers', id);
        const traffic = this.$store.dispatch('getTrafficSources', id);

        Promise.all([offers, traffic])
          .then((res) => {
            this.offer = row.data.affiliateOfferId;
            this.traffic_source_id = row.data.traffic_source_id;
            this.form.subids = this.$_.cloneDeep(row.data.subids);
            this.loading = false;
          });

        this.sub_affiliate_id = row.affiliate_id ? row.sub_affiliate_id : '';
        this.form.promocode = row.promo_code;
        this.id = row.data.id;
      } else {
        this.$store.dispatch('getTrafficSources', {}).then(() => {
          this.loading = false;
        });
      }
    },
    close() {
      this.active = false;
      this.form.promocode = '';
      this.sub_affiliate_id = '';
      this.id = '';
      this.reset();
    },
    reset() {
      this.changed = false;
      this.sending = false;
      this.offer = '';
      this.traffic_source_id = '';
      this.form.subids = [{ value: '' }];
      this.promocode_error = '';
      this.loading = false;
      this.warningOpen = false;
    },
    changeSubAffiliateId() {
      this.reset();
      this.loading = true;
      const id = this.sub_affiliate_id !== '' ? { sub_affiliate_id: this.sub_affiliate_id } : {};
      const offers = this.$store.dispatch('getAffiliateOffers', id);
      const traffic = this.$store.dispatch('getTrafficSources', id);

      Promise.all([offers, traffic])
        .then(() => {
          this.loading = false;
        });
    },
    send() {
      if(this.loading) return;

      this.sending = true;
      this.changed = true;

      const data = {
        traffic_source_id: this.traffic_source_id !== '' ? this.traffic_source_id : null,
        affiliate_offer_id: this.offer,
        promo_code: this.form.promocode,
        status: 'active',
        media_buying_campaign_id: this.$route.params.id,
      };

      this.form.subids.forEach((cur, i) => {
        data[`subid${parseInt(i) + 1}`] = cur.value;
      });

      this.loading = true;
      if (!this.id) {
        this.$store.dispatch('createPromocode', data)
          .then(() => {
            this.close();
            this.$emit('addPromoCode');
            this.$alert.success(this.$t('promoCodes.alerts.wasCreated'));
          })
          .catch((err) => {
            this.errors = err.response.data.errors;
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.$store.dispatch('putPromocode', {
          id: this.id,
          opts: data,
        })
          .then(() => {
            this.close();
            this.$emit('addPromoCode');
            this.$alert.success(this.$t('promoCodes.alerts.wasUpdated'));
          })
          .catch(() => {
            this.$alert.error(this.$t('promoCodes.alerts.couldntCreate'));
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    changeSource(value) {
      if (this.id) {
        if (!value) {
          // if no traffic source selected clear subids
          this.form.subids = [{ value: null }];
          return;
        }
        // subids from promocodes
        let promocodeSubIds = this.$store.state.promocode.promocodes.filter((e) => e.id === this.id)[0];
        promocodeSubIds = _.pick(promocodeSubIds, ['subid1', 'subid2', 'subid3', 'subid4', 'subid5']);

        // subids from ts
        let source = this.$store.state.trafficSource.list.filter((e) => e.data.id === value)[0];
        source = _.pick(source, ['subid1', 'subid2', 'subid3', 'subid4', 'subid5']);

        // subids from old ts
        let oldSource = this.$store.state.trafficSource.list.filter((e) => e.data.id === this.oldValue)[0];
        oldSource = _.pick(oldSource, ['subid1', 'subid2', 'subid3', 'subid4', 'subid5']);

        // take current subids
        const subids = {};
        for (let i = 0; i < 5; i++) {
          const key = `subid${i + 1}`;
          subids[key] = this.form.subids[i] ? this.form.subids[i].value : null;
        }

        // if current subids is equal with subids from promocodes or trafficsources
        if (_.isEqual(subids, oldSource)) {
          this.form.subids = [];
          this.addSubids(source);
        } else {
          this.warningOpen = true;
        }
        this.oldValue = value;
      }
    },
    confirmChanges() {
      this.form.subids = [];
      const source = this.$store.state.trafficSource.list.filter((e) => e.data.id === this.traffic_source_id)[0];
      this.addSubids(source);
    },
    addSubids(source) {
      if (!source) {
        this.form.subids = [{ value: null }];
        return;
      }
      this.form.subids = [];
      // Push subids
      Object.keys(source).map((k) => {
        if (k.startsWith('subid')) {
          const subid_index = parseInt(k.replace('subid', '')) - 1;
          if (source[k]) {
            this.form.subids.push({
              value: source[k],
            });
          } else {
            this.form.subids.push({
              value: null,
            });
          }
        }
      });
      // reduce subids
      for (let i = this.form.subids.length - 1; i >= 1; i--) {
        if (!this.form.subids[i].value) this.form.subids.pop();
        else break;
      }
    },
    addSubId() {
      this.form.subids.push({
        value: '',
      });
    },
    popSubIds() {
      this.form.subids.pop();
    },
  },
};
</script>

<style lang="scss">
// vue3-migration -- '@/assets/theme/common-page-styles.scss'
// @import 'src/assets/theme/default/promocodes/addpromocode';
</style>
